import React from 'react';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import { 
  PanelLeftClose, 
  PanelLeft,
  Users, 
  ListChecks, 
  Layers,
  Moon,
  Sun
} from 'lucide-react';
import { useTheme } from '../../theme/ThemeProvider';

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  onManageTypes: () => void;
  onManageTeam: () => void;
  onManageStatuses: () => void;
  onExit: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isExpanded,
  onToggle,
  onManageTypes,
  onManageTeam,
  onManageStatuses,
}) => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: isExpanded ? 240 : 56,
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        zIndex: 1200,
      }}
    >
      {/* Logo Section */}
      <Box sx={{ 
        p: 1, 
        height: 56, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box 
          component="img"
          src="/logo-small.png" // Replace with your actual logo path
          alt="Product Roadmap"
          sx={{
            height: isExpanded ? 40 : 32, // Adjust these values based on your logo
            width: 'auto',
            transition: 'height 0.2s ease',
            objectFit: 'contain'
          }}
        />
      </Box>

      <Divider />

      {/* Main Actions */}
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Tooltip title={isExpanded ? '' : 'Manage Types'} placement="right">
          <IconButton
            onClick={onManageTypes}
            sx={{
              width: '100%',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Layers size={20} />
            {isExpanded && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Manage Types
              </Box>
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={isExpanded ? '' : 'Manage Team'} placement="right">
          <IconButton
            onClick={onManageTeam}
            sx={{
              width: '100%',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Users size={20} />
            {isExpanded && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Manage Team
              </Box>
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={isExpanded ? '' : 'Manage Statuses'} placement="right">
          <IconButton
            onClick={onManageStatuses}
            sx={{
              width: '100%',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListChecks size={20} />
            {isExpanded && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Manage Statuses
              </Box>
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mt: 'auto', p: 1 }}>
        <Divider sx={{ mb: 1 }} />
        
        {/* Expand/Collapse Button */}
        <Tooltip title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
          <IconButton
            onClick={onToggle}
            sx={{
              width: '100%',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              gap: 1,
              px: 1,
              py: 1,
              mb: 1,
              borderRadius: 1,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isExpanded ? (
              <PanelLeftClose size={20} />
            ) : (
              <PanelLeft size={20} />
            )}
            {isExpanded && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.875rem' }}>
                Collapse Sidebar
              </Box>
            )}
          </IconButton>
        </Tooltip>

        {/* Theme Toggle */}
        <Tooltip title={isExpanded ? '' : `Switch to ${isDarkMode ? 'light' : 'dark'} mode`} placement="right">
          <IconButton
            onClick={toggleTheme}
            sx={{
              width: '100%',
              justifyContent: isExpanded ? 'flex-start' : 'center',
              gap: 1,
              px: 1,
              py: 1,
              borderRadius: 1,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            {isExpanded && (
              <Box component="span" sx={{ ml: 1, fontSize: '0.875rem' }}>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </Box>
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};